'use client';

import { useEffect, useState, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { trackEvent } from '@/lib/analytics';
import type { SearchParams } from './SearchInteractive';

interface FlightResultsProps {
  searchParams: SearchParams;
  activeFilters: {
    maxPrice: number;
    stops: 'any' | '0' | '1' | '2+';
    airlines: string[];
    departTimeRange: 'any' | 'morning' | 'afternoon' | 'evening';
  };
}

interface Flight {
  id: string;
  airline: string;
  airlineCode: string;
  flightNumber: string;
  from: string;
  fromCode: string;
  to: string;
  toCode: string;
  departTime: string;
  arriveTime: string;
  duration: string;
  stops: number;
  stopInfo?: string;
  price: number;
  returnPrice?: number;
  cabinClass: string;
  seatsLeft: number;
  baggage: string;
  refundable: boolean;
  logo: string;
}

const AIRLINES = [
  { name: 'British Airways', code: 'BA', logo: 'BA' },
  { name: 'Emirates', code: 'EK', logo: 'EK' },
  { name: 'Lufthansa', code: 'LH', logo: 'LH' },
  { name: 'Air France', code: 'AF', logo: 'AF' },
  { name: 'KLM', code: 'KL', logo: 'KL' },
  { name: 'Turkish Airlines', code: 'TK', logo: 'TK' },
  { name: 'Qatar Airways', code: 'QR', logo: 'QR' },
  { name: 'Etihad Airways', code: 'EY', logo: 'EY' },
  { name: 'Ryanair', code: 'FR', logo: 'FR' },
  { name: 'easyJet', code: 'U2', logo: 'U2' },
  { name: 'Wizz Air', code: 'W6', logo: 'W6' },
  { name: 'Virgin Atlantic', code: 'VS', logo: 'VS' },
];

const SEAT_COUNTS = [3, 7, 2, 5, 1, 8, 4, 6, 2, 9, 3, 5];
const PRICE_MULTIPLIERS = [1.05, 0.92, 1.12, 0.88, 0.97, 1.08, 0.95, 1.15, 0.78, 0.82, 0.75, 1.02];

const AIRPORT_CODES: Record<string, string> = {
  london: 'LHR', manchester: 'MAN', birmingham: 'BHX', edinburgh: 'EDI',
  glasgow: 'GLA', bristol: 'BRS', leeds: 'LBA', newcastle: 'NCL',
  dubai: 'DXB', 'new york': 'JFK', bangkok: 'BKK', maldives: 'MLE',
  lahore: 'LHE', karachi: 'KHI', toronto: 'YYZ', ibiza: 'IBZ',
  paris: 'CDG', amsterdam: 'AMS', rome: 'FCO', barcelona: 'BCN',
  madrid: 'MAD', lisbon: 'LIS', athens: 'ATH', istanbul: 'IST',
  singapore: 'SIN', tokyo: 'NRT', sydney: 'SYD', los: 'LAX',
  miami: 'MIA', chicago: 'ORD', cancun: 'CUN', cairo: 'CAI',
  doha: 'DOH', 'abu dhabi': 'AUH', mumbai: 'BOM', delhi: 'DEL',
  islamabad: 'ISB', dhaka: 'DAC', colombo: 'CMB', nairobi: 'NBO',
};

const getAirportCode = (city: string): string => {
  const lower = city.toLowerCase().trim();
  for (const [key, code] of Object.entries(AIRPORT_CODES)) {
    if (lower.includes(key)) return code;
  }
  return city.substring(0, 3).toUpperCase();
};

const generateFlights = (params: SearchParams): Flight[] => {
  const fromCode = getAirportCode(params.from);
  const toCode = getAirportCode(params.to);
  const basePrices: Record<string, number> = {
    economy: 189,
    'premium-economy': 449,
    business: 1099,
    first: 2499,
  };
  const base = basePrices[params.cabinClass] || 189;

  // Adjust base price by route distance heuristic
  const longHaulKeywords = ['new york', 'toronto', 'los angeles', 'miami', 'chicago', 'sydney', 'tokyo', 'singapore', 'bangkok', 'maldives', 'cancun'];
  const isLongHaul = longHaulKeywords.some(k =>
    params.from.toLowerCase().includes(k) || params.to.toLowerCase().includes(k)
  );
  const routeMultiplier = isLongHaul ? 1.8 : 1.0;

  const departureTimes = ['05:45', '07:30', '09:15', '11:00', '13:20', '15:45', '18:10', '20:30', '22:00', '06:20', '10:40', '16:55'];
  const durations = ['2h 15m', '3h 05m', '3h 45m', '4h 20m', '5h 10m', '6h 30m', '7h 15m', '8h 00m', '9h 30m', '11h 45m', '2h 50m', '4h 05m'];

  return AIRLINES.map((airline, i) => {
    const departTime = departureTimes[i];
    const durationStr = durations[i];
    const [dHours, dMins] = durationStr.replace('h ', ':').replace('m', '').split(':').map(Number);
    const [depH, depM] = departTime.split(':').map(Number);
    const arrivalMinutes = depH * 60 + depM + dHours * 60 + dMins;
    const arrH = Math.floor(arrivalMinutes / 60) % 24;
    const arrM = arrivalMinutes % 60;
    const arriveTime = `${String(arrH).padStart(2, '0')}:${String(arrM).padStart(2, '0')}`;
    const priceVariation = PRICE_MULTIPLIERS[i];
    const price = Math.round(base * routeMultiplier * priceVariation);
    const stops = i === 0 || i === 2 || i === 8 || i === 10 ? 0 : i === 4 || i === 9 ? 2 : 1;
    const stopLabels = ['via Amsterdam', 'via Frankfurt', 'via Istanbul', 'via Doha', 'via Dubai', 'via Paris'];

    return {
      id: `flight-${i + 1}`,
      airline: airline.name,
      airlineCode: airline.code,
      flightNumber: `${airline.code}${100 + i * 37}`,
      from: params.from,
      fromCode,
      to: params.to,
      toCode,
      departTime,
      arriveTime,
      duration: durationStr,
      stops,
      stopInfo: stops === 1 ? stopLabels[i % stopLabels.length] : stops === 2 ? 'via Dubai, Doha' : undefined,
      price,
      returnPrice: params.tripType === 'round-trip' ? Math.round(price * 1.85) : undefined,
      cabinClass: params.cabinClass,
      seatsLeft: SEAT_COUNTS[i],
      baggage: params.cabinClass === 'economy' ? '23kg hold + 7kg cabin' : '2x 32kg hold + 14kg cabin',
      refundable: i % 3 === 0,
      logo: airline.logo,
    };
  });
};

const buildSkyscannerUrl = (params: SearchParams, flight?: Flight): string => {
  const fromCode = getAirportCode(params.from);
  const toCode = getAirportCode(params.to);
  const dateStr = params.departDate.replace(/-/g, '').substring(2); // YYMMDD
  const adults = params.passengers.adults;
  const children = params.passengers.children;
  const cabinMap: Record<string, string> = {
    economy: 'economy',
    'premium-economy': 'premiumeconomy',
    business: 'business',
    first: 'first',
  };
  const cabin = cabinMap[params.cabinClass] || 'economy';
  const base = `https://www.skyscanner.net/transport/flights/${fromCode}/${toCode}/${dateStr}/`;
  const query = `?adults=${adults}&children=${children}&cabinclass=${cabin}&currency=GBP&locale=en-GB`;
  return base + query;
};

interface LeadModalProps {
  flight: Flight;
  searchParams: SearchParams;
  onClose: () => void;
}

const LeadModal = ({ flight, searchParams, onClose }: LeadModalProps) => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const lead = {
      ...formData,
      flight: {
        airline: flight.airline,
        flightNumber: flight.flightNumber,
        from: flight.from,
        to: flight.to,
        departDate: searchParams.departDate,
        returnDate: searchParams.returnDate,
        departTime: flight.departTime,
        arriveTime: flight.arriveTime,
        duration: flight.duration,
        stops: flight.stops,
        cabinClass: flight.cabinClass,
        price: flight.returnPrice ?? flight.price,
        baggage: flight.baggage,
        passengers: searchParams.passengers,
        tripType: searchParams.tripType,
      },
      timestamp: new Date().toISOString(),
      status: 'new',
    };
    const existing = JSON.parse(localStorage.getItem('bookingLeads') || '[]');
    existing.push(lead);
    localStorage.setItem('bookingLeads', JSON.stringify(existing));

    trackEvent('lead_form_submit', { airline: flight.airline, from: flight.from, to: flight.to });

    // Send WhatsApp message
    const displayPrice = flight.returnPrice ?? flight.price;
    const stopsLabel = flight.stops === 0 ? 'Direct' : flight.stops === 1 ? '1 Stop' : '2 Stops';
    const msg = [
      `Hi! I'd like to book a flight through DIAL FOR FLIGHT.`,
      ``,
      `✈️ *Flight Details*`,
      `Airline: ${flight.airline} (${flight.flightNumber})`,
      `Route: ${flight.from} → ${flight.to}`,
      `Departure: ${searchParams.departDate} at ${flight.departTime}`,
      searchParams.returnDate ? `Return: ${searchParams.returnDate}` : '',
      `Arrival: ${flight.arriveTime} | Duration: ${flight.duration}`,
      `Stops: ${stopsLabel}${flight.stopInfo ? ` (${flight.stopInfo})` : ''}`,
      `Cabin: ${flight.cabinClass.replace('-', ' ')}`,
      `Baggage: ${flight.baggage}`,
      `Passengers: ${searchParams.passengers.adults} Adult(s)${searchParams.passengers.children > 0 ? `, ${searchParams.passengers.children} Child(ren)` : ''}`,
      `Estimated Price: £${displayPrice.toLocaleString()}`,
      ``,
      `👤 *My Details*`,
      `Name: ${formData.name}`,
      `Email: ${formData.email}`,
      `Phone: ${formData.phone}`,
      formData.message ? `Notes: ${formData.message}` : '',
    ].filter(Boolean).join('\n');

    window.open(`https://wa.me/447445270141?text=${encodeURIComponent(msg)}`, '_blank');
    setSubmitted(true);
  };

  const displayPrice = flight.returnPrice ?? flight.price;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {!submitted ? (
          <>
            <div className="bg-gradient-to-r from-primary to-secondary p-6 rounded-t-2xl">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-xl font-black text-white">Book with DIAL FOR FLIGHT</h2>
                  <p className="text-white/80 text-sm mt-1">Expert assistance • Best price guarantee</p>
                </div>
                <button onClick={onClose} className="text-white/70 hover:text-white transition-colors">
                  <Icon name="XMarkIcon" size={24} />
                </button>
              </div>
              {/* Flight Summary */}
              <div className="mt-4 bg-white/10 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-black text-lg">{flight.from} → {flight.to}</p>
                    <p className="text-white/80 text-sm">{flight.airline} · {flight.flightNumber} · {flight.departTime} → {flight.arriveTime}</p>
                    <p className="text-white/70 text-xs mt-1">{flight.duration} · {flight.stops === 0 ? 'Direct' : flight.stops === 1 ? '1 Stop' : '2 Stops'} · {flight.cabinClass}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-black text-accent">£{displayPrice.toLocaleString()}</p>
                    <p className="text-white/70 text-xs">per person</p>
                  </div>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-foreground mb-1.5">Full Name *</label>
                  <input type="text" required value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2.5 border-2 border-border rounded-lg focus:border-primary focus:outline-none text-sm"
                    placeholder="John Smith" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-foreground mb-1.5">Phone *</label>
                  <input type="tel" required value={formData.phone}
                    onChange={e => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-3 py-2.5 border-2 border-border rounded-lg focus:border-primary focus:outline-none text-sm"
                    placeholder="+44 7445 270141" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-bold text-foreground mb-1.5">Email Address *</label>
                <input type="email" required value={formData.email}
                  onChange={e => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2.5 border-2 border-border rounded-lg focus:border-primary focus:outline-none text-sm"
                  placeholder="john@example.com" />
              </div>
              <div>
                <label className="block text-sm font-bold text-foreground mb-1.5">Special Requirements (Optional)</label>
                <textarea value={formData.message}
                  onChange={e => setFormData({ ...formData, message: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2.5 border-2 border-border rounded-lg focus:border-primary focus:outline-none text-sm resize-none"
                  placeholder="Meal preferences, seat requests, etc." />
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-green-600">✓</span>
                  <span className="text-sm font-bold text-green-800">What happens next?</span>
                </div>
                <ul className="text-xs text-green-700 space-y-1">
                  <li>• Our expert contacts you on WhatsApp within 5 minutes</li>
                  <li>• We confirm availability and best price for your flight</li>
                  <li>• Secure payment via bank transfer or card</li>
                  <li>• Instant e-ticket confirmation sent to your email</li>
                </ul>
              </div>

              <button type="submit"
                className="w-full bg-green-600 hover:bg-green-700 text-white py-3.5 rounded-xl font-black text-base transition-colors flex items-center justify-center gap-2 shadow-lg">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Send via WhatsApp & Confirm Booking
              </button>
              <p className="text-center text-xs text-muted-foreground">By submitting, you agree to be contacted by our travel experts</p>
            </form>
          </>
        ) : (
          <div className="p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="CheckCircleIcon" size={48} className="text-green-600" />
            </div>
            <h3 className="text-2xl font-black text-foreground mb-2">Request Sent!</h3>
            <p className="text-muted-foreground mb-4">Our travel expert will contact you on WhatsApp within 5 minutes.</p>
            <div className="bg-primary/5 rounded-xl p-4 text-left mb-6">
              <p className="font-bold text-sm text-foreground mb-2">Booking Summary:</p>
              <p className="text-sm text-muted-foreground">{flight.airline} · {flight.from} → {flight.to}</p>
              <p className="text-sm text-muted-foreground">{searchParams.departDate} · {flight.departTime} → {flight.arriveTime}</p>
              <p className="text-lg font-black text-primary mt-2">£{displayPrice.toLocaleString()} per person</p>
            </div>
            <button onClick={onClose} className="w-full bg-primary text-white py-3 rounded-xl font-black transition-colors hover:bg-primary/90">
              Continue Browsing Flights
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const FlightResults = ({ searchParams, activeFilters }: FlightResultsProps) => {
  const router = useRouter();
  const [flights, setFlights] = useState<Flight[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<'price' | 'duration' | 'stops'>('price');
  const [selectedFlight, setSelectedFlight] = useState<Flight | null>(null);
  const [expandedFlight, setExpandedFlight] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => {
      const generated = generateFlights(searchParams);
      setFlights(generated);
      setLoading(false);
    }, 1400);
    return () => clearTimeout(timer);
  }, [searchParams]);

  const filteredFlights = flights.filter(flight => {
    const displayPrice = flight.returnPrice ?? flight.price;
    if (displayPrice > activeFilters.maxPrice) return false;
    if (activeFilters.stops !== 'any') {
      if (activeFilters.stops === '0' && flight.stops !== 0) return false;
      if (activeFilters.stops === '1' && flight.stops !== 1) return false;
      if (activeFilters.stops === '2+' && flight.stops < 2) return false;
    }
    if (activeFilters.airlines.length > 0 && !activeFilters.airlines.includes(flight.airline)) return false;
    if (activeFilters.departTimeRange !== 'any') {
      const [h] = flight.departTime.split(':').map(Number);
      if (activeFilters.departTimeRange === 'morning' && (h < 5 || h >= 12)) return false;
      if (activeFilters.departTimeRange === 'afternoon' && (h < 12 || h >= 18)) return false;
      if (activeFilters.departTimeRange === 'evening' && h < 18) return false;
    }
    return true;
  });

  const sortedFlights = [...filteredFlights].sort((a, b) => {
    if (sortBy === 'price') return (a.returnPrice ?? a.price) - (b.returnPrice ?? b.price);
    if (sortBy === 'stops') return a.stops - b.stops;
    if (sortBy === 'duration') {
      const parseDuration = (d: string) => {
        const [h, m] = d.replace('h ', ':').replace('m', '').split(':').map(Number);
        return h * 60 + m;
      };
      return parseDuration(a.duration) - parseDuration(b.duration);
    }
    return 0;
  });

  const handleBookWithUs = useCallback((flight: Flight) => {
    trackEvent('book_with_us_click', { airline: flight.airline, from: flight.from, to: flight.to, price: flight.price });
    setSelectedFlight(flight);
  }, []);

  const handleSkyscanner = useCallback((flight: Flight) => {
    trackEvent('skyscanner_click', { airline: flight.airline, from: flight.from, to: flight.to });
    const url = buildSkyscannerUrl(searchParams, flight);
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [searchParams]);

  const totalPassengers = searchParams.passengers.adults + searchParams.passengers.children + searchParams.passengers.infants;

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="bg-card rounded-xl shadow-3d p-6 border-4 border-accent/20">
          <div className="flex items-center gap-3 mb-4">
            <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full"></div>
            <span className="font-bold text-foreground">Searching 500+ airlines for the best prices...</span>
          </div>
          <div className="space-y-3">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-28 bg-surface rounded-lg animate-pulse"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Results Header */}
      <div className="bg-card rounded-xl shadow-3d p-4 border-4 border-accent/20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-black text-foreground">
            {sortedFlights.length} flights found
            {sortedFlights.length < flights.length && <span className="text-sm font-bold text-muted-foreground ml-2">(filtered from {flights.length})</span>}
          </h2>
          <p className="text-sm text-muted-foreground">
            {searchParams.from} → {searchParams.to} · {searchParams.departDate}
            {searchParams.returnDate && ` · Return: ${searchParams.returnDate}`}
            · {totalPassengers} passenger{totalPassengers !== 1 ? 's' : ''}
            · <span className="capitalize">{searchParams.cabinClass.replace('-', ' ')}</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-muted-foreground">Sort:</span>
          {(['price', 'duration', 'stops'] as const).map(opt => (
            <button key={opt} onClick={() => setSortBy(opt)}
              className={`px-3 py-1.5 rounded-lg text-sm font-bold transition-all ${
                sortBy === opt ? 'bg-primary text-white' : 'bg-surface text-foreground hover:bg-muted'
              }`}>
              {opt.charAt(0).toUpperCase() + opt.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Live Price Banner */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-3 flex items-center gap-3">
        <span className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse flex-shrink-0"></span>
        <p className="text-sm text-green-800 font-bold">
          Prices shown are indicative fares in GBP. Final price confirmed at booking. Updated {new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}.
        </p>
      </div>

      {sortedFlights.length === 0 && (
        <div className="bg-card rounded-xl p-8 text-center border-2 border-border">
          <Icon name="MagnifyingGlassIcon" size={40} className="text-muted-foreground mx-auto mb-3" />
          <h3 className="font-black text-foreground mb-2">No flights match your filters</h3>
          <p className="text-muted-foreground text-sm">Try adjusting your filters to see more results</p>
        </div>
      )}

      {/* Flight Cards */}
      {sortedFlights.map((flight) => {
        const displayPrice = flight.returnPrice ?? flight.price;
        const isExpanded = expandedFlight === flight.id;

        return (
          <div key={flight.id} className="bg-card rounded-xl shadow-3d border-2 border-border hover:border-primary/40 transition-all duration-200 overflow-hidden">
            <div className="p-5">
              <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                {/* Airline Info */}
                <div className="flex items-center gap-3 lg:w-44 flex-shrink-0">
                  <div className="w-14 h-14 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-xl flex items-center justify-center border-2 border-border flex-shrink-0">
                    <span className="text-sm font-black text-primary">{flight.airlineCode}</span>
                  </div>
                  <div>
                    <p className="font-black text-sm text-foreground">{flight.airline}</p>
                    <p className="text-xs text-muted-foreground">{flight.flightNumber}</p>
                    {flight.refundable && (
                      <span className="text-xs text-green-600 font-bold">✓ Refundable</span>
                    )}
                  </div>
                </div>

                {/* Flight Times */}
                <div className="flex-1 flex items-center gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-black text-foreground">{flight.departTime}</p>
                    <p className="text-xs font-bold text-muted-foreground">{flight.fromCode}</p>
                    <p className="text-xs text-muted-foreground">{searchParams.from.substring(0, 10)}</p>
                  </div>

                  <div className="flex-1 flex flex-col items-center">
                    <p className="text-xs text-muted-foreground mb-1">{flight.duration}</p>
                    <div className="w-full flex items-center gap-1">
                      <div className="flex-1 h-px bg-border"></div>
                      <Icon name="PaperAirplaneIcon" size={14} className="text-primary" />
                      <div className="flex-1 h-px bg-border"></div>
                    </div>
                    <p className={`text-xs font-bold mt-1 ${
                      flight.stops === 0 ? 'text-green-600' : flight.stops === 1 ? 'text-amber-600' : 'text-red-500'
                    }`}>
                      {flight.stops === 0 ? '✈ Direct' : flight.stops === 1 ? '1 Stop' : '2 Stops'}
                    </p>
                    {flight.stopInfo && <p className="text-xs text-muted-foreground">{flight.stopInfo}</p>}
                  </div>

                  <div className="text-center">
                    <p className="text-2xl font-black text-foreground">{flight.arriveTime}</p>
                    <p className="text-xs font-bold text-muted-foreground">{flight.toCode}</p>
                    <p className="text-xs text-muted-foreground">{searchParams.to.substring(0, 10)}</p>
                  </div>
                </div>

                {/* Price & Book */}
                <div className="flex flex-row lg:flex-col items-center lg:items-end justify-between lg:justify-center gap-3 lg:w-48 flex-shrink-0">
                  <div className="text-right">
                    <p className="text-3xl font-black text-primary">£{displayPrice.toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">
                      {flight.returnPrice ? 'per person (return)' : 'per person (one way)'}
                    </p>
                    {flight.seatsLeft <= 4 && (
                      <p className="text-xs font-black text-red-500 animate-pulse">🔥 Only {flight.seatsLeft} left!</p>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 w-full lg:items-end">
                    <button
                      onClick={() => handleBookWithUs(flight)}
                      className="w-full lg:w-auto bg-primary text-white px-4 py-2.5 rounded-lg font-black text-sm shadow-lg hover:bg-primary/90 active:scale-95 transition-all border-2 border-primary/80 whitespace-nowrap flex items-center justify-center gap-1.5"
                    >
                      <Icon name="PhoneIcon" size={14} />
                      Book with Us
                    </button>
                    <button
                      onClick={() => handleSkyscanner(flight)}
                      className="w-full lg:w-auto bg-[#0770E3] text-white px-4 py-2.5 rounded-lg font-black text-sm hover:bg-[#0660C8] active:scale-95 transition-all whitespace-nowrap flex items-center justify-center gap-1.5"
                    >
                      <Icon name="ArrowTopRightOnSquareIcon" size={14} />
                      Skyscanner
                    </button>
                  </div>
                </div>
              </div>

              {/* Flight Details Row */}
              <div className="mt-4 pt-4 border-t border-border flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Icon name="ShoppingBagIcon" size={14} className="text-accent" />
                  {flight.baggage}
                </span>
                <span className="flex items-center gap-1">
                  <Icon name="TagIcon" size={14} className="text-accent" />
                  <span className="capitalize">{flight.cabinClass.replace('-', ' ')}</span>
                </span>
                <button
                  onClick={() => setExpandedFlight(isExpanded ? null : flight.id)}
                  className="ml-auto flex items-center gap-1 text-primary font-bold hover:underline"
                >
                  {isExpanded ? 'Hide details' : 'View details'}
                  <Icon name={isExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={14} />
                </button>
              </div>

              {/* Expanded Details */}
              {isExpanded && (
                <div className="mt-4 pt-4 border-t border-border grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-xs text-muted-foreground font-bold mb-1">Departure</p>
                    <p className="font-black">{flight.departTime}</p>
                    <p className="text-xs text-muted-foreground">{flight.fromCode} · {searchParams.from}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground font-bold mb-1">Arrival</p>
                    <p className="font-black">{flight.arriveTime}</p>
                    <p className="text-xs text-muted-foreground">{flight.toCode} · {searchParams.to}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground font-bold mb-1">Flight Duration</p>
                    <p className="font-black">{flight.duration}</p>
                    <p className="text-xs text-muted-foreground">{flight.stops === 0 ? 'Non-stop' : `${flight.stops} stop${flight.stops > 1 ? 's' : ''}`}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground font-bold mb-1">Baggage</p>
                    <p className="font-black text-xs">{flight.baggage}</p>
                    <p className="text-xs text-muted-foreground capitalize">{flight.cabinClass.replace('-', ' ')}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Bottom CTA Section */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
        <div className="bg-gradient-to-br from-primary to-secondary rounded-xl shadow-3d p-5 border-2 border-primary/30 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Icon name="PhoneIcon" size={20} className="text-white" />
            <p className="text-base font-black text-white">Book Direct with DIAL FOR FLIGHT</p>
          </div>
          <p className="text-xs text-white/80 mb-4">
            Expert assistance, exclusive deals & personalised service. Response within 5 minutes via WhatsApp.
          </p>
          <button
            onClick={() => {
              trackEvent('book_with_us_banner_click', { from: searchParams.from, to: searchParams.to });
              const params = new URLSearchParams({
                type: 'flight',
                from: searchParams.from,
                to: searchParams.to,
                departDate: searchParams.departDate,
                returnDate: searchParams.returnDate || '',
                passengers: JSON.stringify(searchParams.passengers),
                cabinClass: searchParams.cabinClass,
                tripType: searchParams.tripType,
                bookingSource: 'dial-for-flight',
              });
              window.location.href = `/booking-interest?${params.toString()}`;
            }}
            className="inline-flex items-center gap-2 bg-white text-primary px-5 py-2.5 rounded-lg font-black text-sm hover:bg-white/90 transition-colors shadow-lg"
          >
            <Icon name="PhoneIcon" size={14} />
            Get Expert Help Now
          </button>
        </div>

        <div className="bg-[#0770E3] rounded-xl shadow-3d p-5 border-2 border-[#0660C8] text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Icon name="ArrowTopRightOnSquareIcon" size={20} className="text-white" />
            <p className="text-base font-black text-white">Compare on Skyscanner</p>
          </div>
          <p className="text-xs text-white/80 mb-4">
            Search 1000+ airlines and travel agents. Compare prices and book directly.
          </p>
          <a
            href={buildSkyscannerUrl(searchParams)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent('skyscanner_banner_click', { from: searchParams.from, to: searchParams.to })}
            className="inline-flex items-center gap-2 bg-white text-[#0770E3] px-5 py-2.5 rounded-lg font-black text-sm hover:bg-white/90 transition-colors shadow-lg"
          >
            <Icon name="ArrowTopRightOnSquareIcon" size={14} />
            Search Skyscanner
          </a>
        </div>
      </div>

      {/* Lead Modal */}
      {selectedFlight && (
        <LeadModal
          flight={selectedFlight}
          searchParams={searchParams}
          onClose={() => setSelectedFlight(null)}
        />
      )}
    </div>
  );
};

export default FlightResults;
